/**
 * 建筑入口管理（07 开发文档 /entrances——一期恰 4 项，单源配置）：
 * 双语名称/导览文案、目标类型（站内/外链/未开放）、轮廓/锚点/深度、状态。
 * 校验（04 开发清单）：外链必须 http(s)；COMING_SOON 不产生链接。
 */
import { useCallback, useEffect, useState } from 'react';
import { adminHttp, ApiError } from '../lib/api';
import { Button } from '@chronospeak/ui';
import { ErrorNote, Loading, PageHeader, StatusBadge } from '../components/ui-bits';
import { TranslateButton, MachineDraftBadge } from '../components/TranslateButton';
import { useSession } from '../lib/session';

interface EntranceRow {
  id: string;
  key: string;
  status: string;
  targetType: string;
  target: string | null;
  outlinePath: string;
  labelAnchor: { x: number; y: number };
  depth: number;
  translations: {
    'zh-CN': { label: string; description: string } | null;
    en: { label: string; description: string } | null;
  };
}

export function EntrancesPage() {
  const [items, setItems] = useState<EntranceRow[] | null>(null);
  const [editing, setEditing] = useState<EntranceRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const { admin } = useSession();
  /** 「机翻初稿」脏标记：机翻填充置 true，任一英文字段编辑后清 false */
  const [mtDraft, setMtDraft] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await adminHttp.get<{ items: EntranceRow[] }>('/admin/entrances');
      setItems(res.items);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '加载失败');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (row: EntranceRow) => {
    setError(null);
    setNotice(null);
    if (row.targetType === 'EXTERNAL' && row.target && !/^https?:\/\//i.test(row.target)) {
      return setError('外链必须以 http(s):// 开头。');
    }
    if (row.targetType === 'COMING_SOON' && row.target) {
      return setError('未开放状态不产生链接（清空 target）。');
    }
    const zh = row.translations['zh-CN'];
    if (!zh?.label?.trim()) return setError('名称（中文）必填。');
    try {
      await adminHttp.patch(`/admin/entrances/${row.id}`, {
        key: row.key,
        targetType: row.targetType,
        target: row.targetType === 'COMING_SOON' ? null : row.target,
        outlinePath: row.outlinePath,
        labelAnchor: row.labelAnchor,
        depth: row.depth,
        translations: {
          'zh-CN': { label: zh.label, description: zh.description },
          ...(row.translations.en?.label?.trim()
            ? { en: row.translations.en }
            : {}),
        },
      });
      setNotice(`已保存：${row.key}`);
      await load();
      setEditing(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '保存失败');
    }
  };

  const patchRow = (p: Partial<EntranceRow>) =>
    setEditing((r) => (r ? { ...r, ...p } : r));

  return (
    <section>
      <PageHeader
        title='建筑入口'
        sub='首页建筑、小镇导航弹窗、导览弹窗共用此配置（单源）。一期恰 4 项。'
      />
      {error && <ErrorNote message={error} />}
      {notice && (
        <p role='status' className='mgr-notice'>
          {notice}
        </p>
      )}
      {!items && !error && <Loading />}
      {items && !editing && (
        <div style={{ overflowX: 'auto' }}>
          <table className='audit-table'>
            <thead>
              <tr>
                <th scope='col'>key</th>
                <th scope='col'>名称</th>
                <th scope='col'>目标</th>
                <th scope='col'>状态</th>
                <th scope='col' />
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id}>
                  <td>
                    <code>{e.key}</code>
                  </td>
                  <td>{e.translations['zh-CN']?.label}</td>
                  <td>
                    {e.targetType === 'INTERNAL'
                      ? `站内 ${e.target}`
                      : e.targetType === 'EXTERNAL'
                        ? `外链 ${e.target}`
                        : '未开放'}
                  </td>
                  <td>
                    <StatusBadge status={e.status} />
                  </td>
                  <td>
                    <Button variant='ghost' size='sm' onClick={() => setEditing(e)}>
                      编辑
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <fieldset className='mgr-fieldset' style={{ maxWidth: 640 }}>
          <legend>
            编辑 <code>{editing.key}</code>
          </legend>
          <label className='mgr-field'>
            名称（中文）*
            <input
              value={editing.translations['zh-CN']?.label ?? ''}
              onChange={(e) =>
                patchRow({
                  translations: {
                    ...editing.translations,
                    'zh-CN': {
                      label: e.target.value,
                      description: editing.translations['zh-CN']?.description ?? '',
                    },
                  },
                })
              }
            />
          </label>
          <label className='mgr-field'>
            导览文案（中文）
            <textarea
              rows={2}
              value={editing.translations['zh-CN']?.description ?? ''}
              onChange={(e) =>
                patchRow({
                  translations: {
                    ...editing.translations,
                    'zh-CN': {
                      label: editing.translations['zh-CN']?.label ?? '',
                      description: e.target.value,
                    },
                  },
                })
              }
            />
          </label>
          {editing.translations.en && (
            <>
              <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <TranslateButton
                  mtEnabled={admin?.mtEnabled ?? false}
                  label='翻译导览文案'
                  collectSources={() => [
                    editing.translations['zh-CN']?.label ?? '',
                    editing.translations['zh-CN']?.description ?? '',
                  ]}
                  onBeforeApply={() =>
                    [editing.translations.en?.label, editing.translations.en?.description].every((v) => !v?.trim()) ||
                    window.confirm('英文区已有内容，确定用机器翻译结果覆盖？')
                  }
                  onApply={(translated) => {
                    const [label = '', description = ''] = translated;
                    patchRow({ translations: { ...editing.translations, en: { label, description } } });
                    setMtDraft(true);
                  }}
                />
                <MachineDraftBadge visible={mtDraft} />
              </div>
              <label className='mgr-field'>
                Label (EN)
                <input
                  value={editing.translations.en.label}
                  onChange={(e) => {
                    patchRow({
                      translations: {
                        ...editing.translations,
                        en: { label: e.target.value, description: editing.translations.en!.description },
                      },
                    });
                    setMtDraft(false);
                  }}
                />
              </label>
              <label className='mgr-field'>
                Description (EN)
                <textarea
                  rows={2}
                  value={editing.translations.en.description}
                  onChange={(e) => {
                    patchRow({
                      translations: {
                        ...editing.translations,
                        en: { label: editing.translations.en!.label, description: e.target.value },
                      },
                    });
                    setMtDraft(false);
                  }}
                />
              </label>
            </>
          )}
          <label className='mgr-field'>
            目标类型
            <select value={editing.targetType} onChange={(e) => patchRow({ targetType: e.target.value })}>
              <option value='INTERNAL'>站内页</option>
              <option value='EXTERNAL'>外链</option>
              <option value='COMING_SOON'>未开放</option>
            </select>
          </label>
          {editing.targetType !== 'COMING_SOON' && (
            <label className='mgr-field'>
              目标（站内路由或外链 URL）
              <input value={editing.target ?? ''} onChange={(e) => patchRow({ target: e.target.value })} />
            </label>
          )}
          <label className='mgr-field'>
            标签锚点 x（底图坐标系）
            <input
              value={String(editing.labelAnchor.x)}
              onChange={(e) =>
                patchRow({ labelAnchor: { ...editing.labelAnchor, x: Number(e.target.value) || 0 } })
              }
              inputMode='numeric'
            />
          </label>
          <label className='mgr-field'>
            标签锚点 y
            <input
              value={String(editing.labelAnchor.y)}
              onChange={(e) =>
                patchRow({ labelAnchor: { ...editing.labelAnchor, y: Number(e.target.value) || 0 } })
              }
              inputMode='numeric'
            />
          </label>
          <label className='mgr-field'>
            深度 zIndex
            <input
              value={String(editing.depth)}
              onChange={(e) => patchRow({ depth: Number(e.target.value) || 0 })}
              inputMode='numeric'
            />
          </label>
          <label className='mgr-field'>
            轮廓 path（SVG，底图坐标系；改动影响首页裁切）
            <textarea
              rows={2}
              value={editing.outlinePath}
              onChange={(e) => patchRow({ outlinePath: e.target.value })}
            />
          </label>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <Button variant='primary' size='sm' onClick={() => void save(editing)}>
              保存
            </Button>
            <Button variant='ghost' size='sm' onClick={() => setEditing(null)}>
              取消
            </Button>
          </div>
        </fieldset>
      )}
    </section>
  );
}
